/**
 * Notification — an announcement addressed to one person, at
 * `users/{uid}/notifications/{token}`.
 *
 * Today one kind exists: an invitation. The invite collection declares no
 * client read, so this row is the only in-app surface that tells a person
 * "you were invited" — the invitation email is the floor beneath it, and an
 * address with no account yet has that floor alone. Signing in with the
 * invited address announces it (mintSession → announceInvitationsFor), the
 * invite trigger keeps it true to the invite's own status, and a daily
 * sweep re-announces what a failed write missed. Nothing joins the person
 * until they answer: `acceptInvitation {orgId}` writes the grant and deletes
 * this row, `declineInvitation {orgId}` sets `declined` and deletes it; a
 * revoke or expiry deletes it through the trigger. A row exists exactly
 * while its invite is `pending` and unexpired.
 *
 * Self-only, read-only: the path is the whole authorization boundary (the
 * shape `users/{uid}/grants` already uses). No client write at all — every
 * act that closes a row is a server act.
 *
 * Why the doc id is the invite token: every writer keys the row on the
 * invite it announces, so a second sign-in for the same pending invite
 * writes the same id and re-mints nothing. The token is an internal handle,
 * not a secret — it grants nothing to whoever holds it (the OTP code is what
 * proves inbox possession), and no action takes it as an argument: accept
 * and decline are addressed by `orgId`.
 *
 * Fields justified:
 *   kind          — what the row announces; the one card the app renders
 *                   dispatches on it, and a second kind lands beside it
 *                   without re-reading every stored row.
 *   token         — doc id, mirrored; the join back to
 *                   `workspaces/{orgId}/invites/{token}`.
 *   orgId         — the organization the invite is into, and what accept
 *                   and decline address. The user path names no
 *                   organization, so this is the only place the row carries
 *                   one.
 *   workspaceName / roleName / invitedBy — Invite.InviteProjectionSchema:
 *                   the invitee holds no grant until they accept, so the
 *                   workspace card, the role catalog and the inviter's user
 *                   doc are all unreadable to them and no client join can
 *                   recover a name.
 *   createdAt     — mirrors the invite's own `createdAt`: orders the list
 *                   and dates the card. Mirrored, not stamped, so a
 *                   re-announce reproduces the identical row.
 *   founding      — the invitation is the owner invitation a staff approval
 *                   founded the organization with, so accepting it
 *                   concludes the founder's contract with the operator and
 *                   the card shows the operator's platform terms and privacy
 *                   notice to accept with it. Derived by the announcer; the
 *                   accept re-derives it, so a stale flag gates nothing.
 *
 * Excluded:
 *   status / outcome — a row exists only while the invite is pending; the
 *                   answer deletes it, so there is no closed state to carry.
 *   roleId / email — the invite owns them; the person needs the names, not
 *                   the ids, and their own address is the token's claim.
 *   seen / read   — the count of open invitations is the count of rows.
 *   title / body  — a stored sentence is stored in one language; the kind
 *                   and its fields are what the catalog composes it from.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { InviteProjectionSchema } from "./invite.entity.ts";
import { type Actor, Id } from "./workspace.entity.ts";

export const NotificationSchema = InviteProjectionSchema.extend({
	kind: z.literal("invitation"),
	token: Id.describe(
		"doc id — the invite token; the join back to workspaces/{orgId}/invites/{token}",
	),
	orgId: Id.describe("the organization the invitation is into"),
	createdAt: z.custom<Timestamp>().describe("when the invite was created"),
	founding: z
		.boolean()
		.nullish()
		.describe(
			"the owner invitation a staff approval founded the organization with",
		),
});
export type Notification = z.infer<typeof NotificationSchema>;

// read + list: self only, keyed on the {uid} path variable — the same shape
// Grant's read/list carry. mfa is not required: an account mid-enrollment
// must still see what it was invited to. No write actions declared: the
// functions workspace owns this collection.
export const Notification = entity<Actor>()({
	schema: NotificationSchema,
	actions: ["read", "list"] as const,
	namespace: "tickets.notifications",
})
	.pipe(({ can, pathVar }) => [
		can("read").when((_data, me) => me.uid.eq(pathVar("uid"))),
		can("list").when((_data, me) => me.uid.eq(pathVar("uid"))),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "users/{uid}/notifications/{token}",
		}),
	);
